import type { SliderProps } from '@change/ds-forms';

import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PromoteFormFeatureConfigFieldsFragment } from '../shared/api/promoteFormFeatureConfigFields.graphql';
import type { PromoteFormViewerFieldsFragment } from '../shared/api/promoteFormViewerFields.graphql';

import type { PromoteFormData } from './context';
import { normalizeUserData } from './utils/normalizeUserData';

export type Petition = {
	id: string;
	displayTitle: string;
	slug: string;
	ask: string;
	categoryTags: string[];
	geoReach: string | null;
	relevantLocationLocalizedName: string | null;
	promotionSettings?: {
		cpm: number | null;
		promotionToken: string | null;
		bonusCpm?: number | null;
		bonusThreshold?: number | null;
	} | null;
	feeds: {
		id: string;
		heroFeed: ReadonlyArray<{
			userId: string;
			userDisplayName: string;
			userPhoto: string | null;
			event:
				| {
						__typename: 'HeroFeedPromotionEvent';
						id: string;
						createdAt: string;
						amountUsd: number;
						amount: number;
						amountLocalizedWithCurrency: string | null;
				  }
				| {
						__typename: 'HeroFeedShareEvent';
						id: string;
						createdAt: string;
				  };
		}>;
	};
	user: {
		id: string;
	};
};

function localizeAmount(
	amount: number | null | undefined,
	currencyCode: string,
	utilityContext: UtilityContext,
): { amount: number; localizedAmount: string } | null {
	if (!amount) return null;
	return {
		amount,
		localizedAmount: utilityContext.i18n.localizeCurrency(amount, currencyCode, {
			currencyDisplay: 'narrowSymbol',
			amountInBaseUnits: false,
		}),
	};
}

function normalizePetition(petition: Petition): PromoteFormData['petition'] {
	const {
		id,
		slug,
		ask,
		displayTitle,
		categoryTags,
		geoReach,
		relevantLocationLocalizedName,
		feeds: { heroFeed },
	} = petition;
	return {
		id,
		ask,
		slug,
		displayTitle,
		categoryTags,
		geoReach,
		relevantLocationLocalizedName,
		feeds: { heroFeed: [...heroFeed] },
		user: {
			id: petition.user.id,
		},
	};
}

function normalizePromotionCountrySettings({
	viewer,
	currencyCode,
	utilityContext,
}: {
	viewer: PromoteFormViewerFieldsFragment;
	currencyCode: string;
	utilityContext: UtilityContext;
}): PromoteFormData['promotionCountrySettings'] | null {
	if (!viewer.country.promotionCountrySettings) return null;
	const { amountChoices, anchorAmount, minimumAmount, maximumAmount, confirmationAmount } =
		viewer.country.promotionCountrySettings;

	return {
		amountChoices: amountChoices.map((amount) => {
			return {
				amount,
				localizedAmount: utilityContext.i18n.localizeCurrency(amount, currencyCode, {
					currencyDisplay: 'narrowSymbol',
					amountInBaseUnits: false,
				}),
			};
		}),
		anchorAmount: localizeAmount(anchorAmount, currencyCode, utilityContext),
		minimumAmount: localizeAmount(minimumAmount, currencyCode, utilityContext),
		maximumAmount: localizeAmount(maximumAmount, currencyCode, utilityContext),
		confirmationAmount: localizeAmount(confirmationAmount, currencyCode, utilityContext),
	};
}

function normalizePromotionSettings(petition: Petition): PromoteFormData['promotionSettings'] | null {
	if (!petition.promotionSettings || !petition.promotionSettings.cpm || !petition.promotionSettings.promotionToken)
		return null;
	return {
		cpm: petition.promotionSettings.cpm,
		promotionToken: petition.promotionSettings.promotionToken,
		bonusCpm: petition.promotionSettings.bonusCpm,
		bonusThreshold: petition.promotionSettings.bonusThreshold,
	};
}

function normalizeSubscriptionConfiguration(
	subscriptionConfiguration: PromoteFormFeatureConfigFieldsFragment['subscriptionConfiguration'],
): PromoteFormData['subscriptionConfiguration'] | null {
	if (!subscriptionConfiguration) return null;
	return {
		amounts: [...subscriptionConfiguration.amounts],
		defaultAmount: subscriptionConfiguration.defaultAmount,
		minAmount: subscriptionConfiguration.minAmount,
		maxAmount: subscriptionConfiguration.maxAmount,
		minSliderAmount: subscriptionConfiguration.minSliderAmount,
		maxSliderAmount: subscriptionConfiguration.maxSliderAmount,
		sliderStep: subscriptionConfiguration.sliderStep,
		sliderLayout: subscriptionConfiguration.sliderLayout as SliderProps['layout'],
	};
}

function getCountryAndCurrencyCode(viewer: PromoteFormViewerFieldsFragment): {
	countryCode: string;
	currencyCode: string;
} {
	const countryCode = viewer.user?.country?.countryCode ?? 'US';
	const currencyCode = viewer.country.currency?.code ?? 'USD';
	return { countryCode, currencyCode };
}

export function normalizePromoteFormData({
	petition,
	viewer,
	featureConfigs,
	utilityContext,
}: {
	petition: Petition;
	viewer?: PromoteFormViewerFieldsFragment;
	featureConfigs?: PromoteFormFeatureConfigFieldsFragment;
	utilityContext: UtilityContext;
}): {
	promoteFormData: PromoteFormData | null;
} {
	if (!viewer || !featureConfigs) return { promoteFormData: null };
	const { currencyCode, countryCode } = getCountryAndCurrencyCode(viewer);
	const promotionCountrySettings = normalizePromotionCountrySettings({
		viewer,
		currencyCode,
		utilityContext,
	});
	const promotionSettings = normalizePromotionSettings(petition);

	const { subscriptionConfiguration, ...restFeatureConfigs } = featureConfigs;
	if (promotionCountrySettings && promotionSettings) {
		return {
			promoteFormData: {
				petition: normalizePetition(petition),
				promotionCountrySettings,
				promotionSettings,
				currency: {
					code: currencyCode,
					exchangeRate: viewer.country.currency?.exchangeRate ?? 1,
				},
				featureConfigs: restFeatureConfigs,
				subscriptionConfiguration: normalizeSubscriptionConfiguration(subscriptionConfiguration),
				user: normalizeUserData(viewer),
				amountChoices: promotionCountrySettings.amountChoices,
				membershipProgram: viewer.country.membershipProgram,
				guestUserData: viewer.guestUserData,
				countryCode,
			},
		};
	}
	return { promoteFormData: null };
}
